import {
  PatchedReturnUserWritable,
  ReturnUserReadable,
  usersAllListOptions,
  usersAllListQueryKey,
  usersPartialUpdateMutation
} from '@crm/api'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { getUserFullName } from '@crm/utils'
import {
  ConditionalText,
  TableContainer,
  TitleSection
} from '@shared/components'
import { useInvalidatePredicateQueries, useQueryParams } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { defaultFilters, showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Select } from 'antd'
import { useMemo } from 'react'

import { useStyles } from './styles'

import type { TableColumnsType } from 'antd'

export const Agents = () => {
  const { t } = useLocales()
  const sorter = useTableSorter()
  const { styles } = useStyles()

  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { notification } = App.useApp()

  const params = useQueryParams()

  const { data, isLoading } = useQuery(
    usersAllListOptions({
      query: {
        ...defaultFilters,
        ...params
      }
    })
  )

  const { mutate } = useMutation({
    ...usersPartialUpdateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })

      invalidateQueries([usersAllListQueryKey()])
    }
  })

  const onChangeValue = ({
    id,
    sip,
    phone_number,
    archived,
    ...params
  }: PatchedReturnUserWritable) =>
    mutate({
      path: { id: String(id) },
      body: {
        ...(sip && { sip }),
        ...(phone_number && { phone_number }),
        ...(archived !== undefined && { archived }),
        ...params
      }
    })

  const selectOptions = [
    {
      value: true,
      label: t('buttons.yes')
    },
    {
      value: false,
      label: t('buttons.no')
    }
  ]

  const columns: TableColumnsType<ReturnUserReadable> = useMemo(
    () => [
      {
        title: t('agents.name'),
        dataIndex: 'name',
        key: 'first_name',
        sorter: true,
        render: (_, { email, first_name, last_name }) => (
          <>
            {getUserFullName({ first_name, last_name })} {email}
          </>
        )
      },

      {
        title: t('agents.phone'),
        width: 200,
        dataIndex: 'phone_number',
        key: 'phone_number',
        sorter: true,
        render: (phone_number, { id }) => (
          <ConditionalText
            condition={phone_number}
            className={styles.editCell}
            editable={{
              text: `${phone_number || ''}`,
              onChange: (value) => {
                const digitsOnly = value.replace(/\D/g, '')
                onChangeValue({ id, phone_number: Number(digitsOnly) })
              }
            }}
          >
            {phone_number}
          </ConditionalText>
        )
      },

      {
        title: t('agents.sip'),
        render: ({ sip, id }) => (
          <ConditionalText
            condition={sip}
            className={styles.editCell}
            editable={{
              text: `${sip || ''}`,
              onChange: (value) => onChangeValue({ id, sip: value })
            }}
          >
            {sip}
          </ConditionalText>
        )
      },

      {
        title: t('agents.archived'),
        render: ({ archived, id }) => (
          <Select
            onChange={(value) =>
              onChangeValue({ id, archived: Boolean(value) })
            }
            options={selectOptions}
            className={styles.select}
            defaultValue={archived}
          />
        )
      }
    ],
    [t]
  )

  return (
    <>
      <TitleSection title={t(`menu.agents`)} count={data?.count || 0} />

      <TableContainer
        loading={isLoading}
        data={data}
        columns={columns}
        onChange={(_, __, result) => sorter(result)}
        showSorterTooltip={false}
      />
    </>
  )
}
